import { Tag, Wrap, WrapItem } from '@chakra-ui/react';
import { FC, KeyboardEvent, useState } from 'react';
import { MBox, MInput, MTagCloseButton } from '../Monetize';

interface MultiValueInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  isDisabled?: boolean;
  /**
   * Most values the input accepts. At capacity the text input is not rendered,
   * so no more can be added; existing values stay removable.
   */
  maxValues?: number;
}

export const MultiValueInput: FC<MultiValueInputProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Type a value and press Enter',
  isDisabled,
  maxValues,
}) => {
  const [draft, setDraft] = useState('');
  const isAtCapacity = maxValues !== undefined && value.length >= maxValues;

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      return;
    }
    if (value.includes(trimmed)) {
      setDraft('');
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <MBox>
      {!isAtCapacity && (
        <MInput
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            commit();
            onBlur?.();
          }}
          placeholder={placeholder}
          isDisabled={isDisabled}
        />
      )}
      {value.length > 0 && (
        <Wrap gap={1} mt={isAtCapacity ? 0 : 1}>
          {value.map((item, index) => (
            <WrapItem key={`${item}-${index}`}>
              {/* Transparent fill, as v2 rendered it: "tGray.support" isn't a
                  palette, so v3's `solid` fell back to a near-black fill. */}
              <Tag.Root
                size="sm"
                borderWidth="1px"
                borderStyle="solid"
                borderColor="tGray.lightPurple"
                borderRadius="3"
                variant="neutral"
                color="tPurple.base"
              >
                <Tag.Label>{item}</Tag.Label>
                {!isDisabled && (
                  <MTagCloseButton onClick={() => removeAt(index)} />
                )}
              </Tag.Root>
            </WrapItem>
          ))}
        </Wrap>
      )}
    </MBox>
  );
};
